import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { DataService, AuthService } from '../../services';

/**
 * Custom validator to ensure password contains at least one letter and one number
 */
function passwordComplexityValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) {
    return null;
  }
  
  const hasLetter = /[a-zA-Z]/.test(value);
  const hasNumber = /[0-9]/.test(value);
  
  if (!hasLetter || !hasNumber) {
    return { passwordComplexity: true };
  }
  
  return null;
}

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatCardModule
  ],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss'
})
export class ChangePasswordComponent implements OnInit {
  changePasswordForm: FormGroup;
  hideCurrentPassword = true;
  hideNewPassword = true;
  hideConfirmPassword = true;
  isChangingPassword = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private dataService: DataService,
    private authService: AuthService,
    private snackBar: MatSnackBar
  ) {
    this.changePasswordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.minLength(8), passwordComplexityValidator]],
      confirmPassword: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    // Ensure user is authenticated
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/login']);
    }
  }

  onSubmit() {
    if (this.changePasswordForm.valid) {
      const currentPassword = this.changePasswordForm.get('currentPassword')?.value;
      const newPassword = this.changePasswordForm.get('newPassword')?.value;
      const confirmPassword = this.changePasswordForm.get('confirmPassword')?.value;

      if (newPassword !== confirmPassword) {
        this.snackBar.open(
          'New passwords do not match.',
          '×',
          {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
        return;
      }

      if (currentPassword === newPassword) {
        this.snackBar.open(
          'New password must be different from current password.',
          '×',
          {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
        return;
      }

      this.isChangingPassword = true;
      this.dataService.changePassword(currentPassword, newPassword).subscribe({
        next: (success) => {
          this.isChangingPassword = false;
          if (success) {
            this.snackBar.open(
              'Password changed successfully!',
              '×',
              {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                duration: 5000,
                panelClass: ['success-snackbar']
              }
            );
            // Navigate back to dashboard
            this.router.navigate(['/dashboard']);
          } else {
            this.snackBar.open(
              'Failed to change password. Please check your current password.',
              '×',
              {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              }
            );
          }
        },
        error: (error) => {
          this.isChangingPassword = false;
          console.error('Password change error:', error);
          const errorMessage = error?.error?.message || 'An error occurred. Please try again.';
          this.snackBar.open(
            errorMessage,
            '×',
            {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );
        }
      });
    }
  }

  onCancel() {
    this.router.navigate(['/dashboard']);
  }

  toggleCurrentPasswordVisibility() {
    this.hideCurrentPassword = !this.hideCurrentPassword;
  }

  toggleNewPasswordVisibility() {
    this.hideNewPassword = !this.hideNewPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.hideConfirmPassword = !this.hideConfirmPassword;
  }
}
